import { ActorRefFrom, assign, createMachine } from 'xstate'

import { buildEventDescription } from '../lib/mockDigitalData'
import { PaymentFrequencyValues, PaymentFrequency, SelectOptions } from '../types/global'
import { emailReportMachine } from './emailReport.machine'
import { trackingService } from './tracking.machine'

export interface CalculatorContext {
  costPerUnit: number
  emissionFactor: number
  carEmissionFactor: number
  gst: number
  annualConsumption: number
  naturalPowerRatio: number
  paymentFrequency: PaymentFrequencyValues
  currentCost: string
  paymentFrequencyOptions: SelectOptions<PaymentFrequencyValues>
  annualConsumptionOptions: SelectOptions<number, { averageCost: number }>
  emailRef?: ActorRefFrom<typeof emailReportMachine>
}

export type CalculatorInputs = Pick<
  CalculatorContext,
  'annualConsumption' | 'naturalPowerRatio' | 'paymentFrequency' | 'currentCost'
>

export type CalculatorEvent = {
  type: 'Update inputs'
} & Partial<CalculatorInputs>

export const calculatorMachine =
  /** @xstate-layout N4IgpgJg5mDOIC5QGECGAbAxgV3agLgPYBOAdAGoYCWEBVAdlAMQDaADALqKgAOhsVfFUL1uIAB6IAtAGYAHDNIBGACwB2AGxs2agJwqArCo0qANCACe0mQCYNytWrlK5Ttgd1qbBgL4-zaFi4BCQU1LRCjKxKXEggfAJCImKSCLJqimwybCpKtvpGJuZWaTZK9nJybEqqunaqOX4BGDh4RMRMAKo8EWAABAw82Piw7LG8-ILConGp6QakZXp5NgXGZpbSWrqkBnI2XtVa3iq6fv4g9IQQcGKBrSFklOg0dIxiCVPJs9K5SqS6GQaYH5QzrYq-BSkDReQy6Ty6KoafRNED3YLtMIvCAfSZJGagOY1f7ZIGrAx5GQU9QQ0pKHaVaq1eoqRoXdFtUIASXoADdwrjEtMUtIampFkpNHVqjIqao1LTZDIVKRWXo1KcqZ5VqiOY9BV8CRJfh4AUCQaswUVNmlkXJdvtDuU2CczucgA */
  createMachine(
    {
      context: {
        costPerUnit: 0,
        emissionFactor: 0,
        carEmissionFactor: 0,
        gst: 0,
        annualConsumption: 0,
        naturalPowerRatio: 0,
        paymentFrequency: PaymentFrequency.Annually,
        currentCost: '0',
      } as CalculatorContext,
      tsTypes: {} as import('./calculator.machine.typegen').Typegen0,
      schema: { context: {} as CalculatorContext, events: {} as CalculatorEvent, services: {} as never },
      preserveActionOrder: true,
      predictableActionArguments: true,
      on: {
        'Update inputs': {
          target: '.Validating',
        },
      },
      id: 'Calculator',
      initial: 'Validating',
      states: {
        Validating: {
          entry: ['Save input values', 'Update tracking data'],
          always: [
            {
              target: 'Invalid',
              cond: 'Cost is invalid',
            },
            {
              target: 'Valid',
            },
          ],
        },
        Valid: {},
        Invalid: {},
      },
    },
    {
      actions: {
        'Save input values': assign(({ emailRef, ...ctx }, { type: _, ...inputs }) => {
          if (emailRef?.getSnapshot()?.matches('Enabled')) {
            emailRef?.send({ type: 'Update inputs' })
          }
          const context = {
            ...ctx,
            ...inputs,
          }
          return context
        }),
        'Update tracking data': (ctx) => {
          trackingService.send({
            type: 'Update description',
            description: buildEventDescription(ctx),
          })
        },
      },
      guards: {
        'Cost is invalid': ({ currentCost }) => isNaN(Number(currentCost)),
      },
    },
  )
